import { midiToNoteName } from '../../../../../common/src/learn_rules.ts';
import {
  directionNotes,
  DoneState,
  isHearingTone,
  isHinted,
  isNoisy,
  isTroubleHearing,
  ListenState,
  RangeDirection,
  StepState,
} from './range_finder_session.ts';

// What the stairs scene draws, worked out from one state of the Range Finder's machine (issue
// #3510, addendum 3). Pure: no React, no DOM, no pixels - the scene owns the geometry, this owns
// what there is to draw. The scene is the only channel of instruction on the direction screen now,
// so every word the singer reads while a direction runs is decided here and unit-tested. What it
// says is steadied twice over: `holding` follows the machine's smoothed hearing (isHearingTone)
// rather than the hold itself, and the bubble the screen finally shows is the one the stabilizer
// (range_bubble_stabilizer.ts) lets through.
//
// The stairs are one tread per note credited in this direction, in order, and the singer stands on
// the tread of the last one (tread 0, unlabelled, is where the direction began). The tread ahead of
// him is the target while the machine is playing it and listening for it. A direction that has
// ended puts him on the landing instead.

export type StairsBubbleKind =
  | 'sing'
  | 'hold'
  | 'match'
  | 'hint'
  | 'trouble'
  | 'noisy'
  | 'done'
  | 'nice' // the hop's own line
  | 'announce'; // the handover's

// One line, and a smaller second line where the situation has more to say
export interface StairsBubble {
  kind: StairsBubbleKind;
  text: string;
  small?: string;
}

export interface StairsSceneModel {
  direction: RangeDirection;
  notes: string[]; // the credited notes' names, in order: one labelled tread each
  onLanding: boolean; // the direction is over: he is on the landing, which carries its own label
  target?: string; // the note being played for him to match, lit on the tread ahead
  holding: boolean; // a tone is being held: the sound waves at his mouth, and "Hold it"
  bubble: StairsBubble;
}

// Treads drawn ahead of the flight's scroll, the last of which is flush with the landing and so
// carries no label of its own: five labelled treads are in view at once.
export const treadsInView = 5;

// Which five treads are in view. The flight scrolls only far enough to keep the tread ahead of the
// singer - the one the target lights up - among the labelled ones, which is what makes the stairs
// endless: he never stands on the last tread drawn, and a tread is always waiting for the next note.
export function scrollFor(credited: number): number {
  return Math.max(0, credited + 2 - treadsInView);
}

// Where the flight has scrolled to, and which tread the singer stands on: both follow from the
// notes credited so far, so neither is stored on the model - one model can only ever mean one of
// each, and a fixture that set them apart would be describing a scene that cannot happen.
export function scrollOf(model: StairsSceneModel): number {
  return scrollFor(model.notes.length);
}

export function treadOf(model: StairsSceneModel): number {
  return model.onLanding ? landingTread(scrollOf(model)) : model.notes.length;
}

// The tread flush with the landing, which is where the singer hops when the direction ends. The
// scroll runs through fractions while a hop scrolls the flight, and the landing is the same tread
// throughout that hop: it is the treads that slide under it, not the landing that moves.
export function landingTread(scroll: number): number {
  return Math.floor(scroll + scrollEpsilon) + treadsInView;
}

// A scroll of 1 arrived at by adding fractions can land a hair under 1; floor() must not read that
// as 0 and drop a tread for one frame
const scrollEpsilon = 1e-6;

// "as low as you can" / "as high as you can", "a little lower" / "a little higher"
const directionWords: Record<RangeDirection, { far: string; further: string }> = {
  lower: { far: 'low', further: 'lower' },
  higher: { far: 'high', further: 'higher' },
};

// Everything the singer is told while a direction runs, in the order the PRD's table gives:
// listening, a held tone wins over the two kinds of nothing heard, which win over the prompt;
// stepping, a held tone wins over the hint, which wins over the question.
function bubbleFor(state: ListenState | StepState | DoneState, holding: boolean): StairsBubble {
  const words = directionWords[state.direction];
  if (state.phase === 'done') {
    return { kind: 'done', text: 'Great job!', small: `That's as ${words.far} as you go` };
  }
  if (holding) {
    return { kind: 'hold', text: 'Hold it' };
  }
  if (state.phase === 'listen') {
    if (isTroubleHearing(state)) {
      return { kind: 'trouble', text: "I'm having trouble hearing you", small: 'Move closer or sing a little louder' };
    }
    if (isNoisy(state)) {
      return { kind: 'noisy', text: "It's a bit noisy in here", small: 'Try a quieter spot' };
    }
    return { kind: 'sing', text: `Sing "La" as ${words.far} as you can`, small: '…and hold it' };
  }
  if (isHinted(state)) {
    return { kind: 'hint', text: `Try a little ${words.further}` };
  }
  // The nudge is the line the silence earns, and it goes as soon as the singer sings again
  return {
    kind: 'match',
    text: 'Can you sing this one?',
    small: state.nudged ? 'Sing "La" along with the note' : undefined,
  };
}

// What the singer says while he is in the air, which the choreography puts over whatever the state
// would otherwise have him say (issue #3510, addendum 3). Here rather than in the choreography
// because the bubble's words all live in one place, and are all tested in one place.
export function hopBubble(direction: RangeDirection): StairsBubble {
  return { kind: 'nice', text: 'Nice one!', small: `one step ${direction === 'lower' ? 'down' : 'up'}\u2026` };
}

// And what he says once the handover has carried him to the foot of the ascent. Only the descent
// ever hands over, so this line needs no direction.
export const announcementBubble: StairsBubble = {
  kind: 'announce',
  text: "Now let's go higher!",
  small: 'Up we go',
};

export function sceneModelFor(state: ListenState | StepState | DoneState): StairsSceneModel {
  const notes = directionNotes(state).map(midiToNoteName);
  // A tone being heard rather than a hold in progress (issue #3510, addendum 4): the machine starts
  // the hold over whenever the pitch drifts or the detector's confidence dips, and the singer who
  // is still singing through that should not watch the bubble flicker
  const holding = isHearingTone(state);
  return {
    direction: state.direction,
    notes,
    onLanding: state.phase === 'done',
    // While he is holding the note it is his to keep, not one to reach for, so the tread ahead
    // stops advertising it and the bubble says "Hold it" instead
    target: state.phase === 'step' && !holding ? midiToNoteName(state.targetMidi) : undefined,
    holding,
    bubble: bubbleFor(state, holding),
  };
}
